import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '@env/environment';
import { extractFilterOptions } from '@app/utils/helpers/filter-extraction.helper';
import { InventoryFilterOptions } from '@app/utils/interfaces/filter-options.interface';
import {
  CreateInventoryItemDto,
  InventoryItemCreatedResponse,
  InventoryItemDetail,
  InventoryItemFullInformation,
  InventoryPaginatedResponse,
  InventoryQueryParams,
  SortedInventoryOptions,
  SortedOptions,
  UpdateInventoryItem,
} from '@app/utils/interfaces/inventory-item.interface';
import { ApiEndpoints } from '@app/utils/enums/api-endpoints';
import { getFilledParams } from '@app/utils/helpers/request.helpers';

@Injectable({
  providedIn: 'root',
})
export class InventoryService {
  private readonly baseUrl: string = environment.apiBaseUrl;
  private readonly http: HttpClient = inject(HttpClient);
  public readonly refreshTrigger = signal(0);
  public isEmptyInventory = signal(false);

  public notifyRefresh(): void {
    this.refreshTrigger.update((val) => val + 1);
  }

  public setIsEmpty(value: boolean): void {
    this.isEmptyInventory.set(value);
  }

  public getInventoryItems(params?: InventoryQueryParams): Observable<InventoryPaginatedResponse> {
    return this.http.get<InventoryPaginatedResponse>(
      `${this.baseUrl}${ApiEndpoints.GET_INVENTORY_ITEMS}`,
      { params: getFilledParams(params) },
    );
  }

  public getSortedInventoryItems(
    params: SortedInventoryOptions,
  ): Observable<InventoryPaginatedResponse> {
    let httpParams = new HttpParams()
      .set('pageNumber', params.pageNumber.toString())
      .set('pageSize', params.pageSize.toString());

    const sortingOptions = params['SortingOptions'];

    if (Array.isArray(sortingOptions)) {
      sortingOptions.forEach((element: SortedOptions) => {
        httpParams = httpParams.append('SortingOptions', JSON.stringify(element));
      });
    }

    return this.http.get<InventoryPaginatedResponse>(
      `${this.baseUrl}${ApiEndpoints.GET_INVENTORY_ITEMS}`,
      { params: httpParams },
    );
  }

  public getFilterOptions(): Observable<InventoryFilterOptions> {
    return this.getInventoryItems().pipe(
      map((response) => {
        const items = response?.data?.items ?? [];
        return extractFilterOptions(items);
      }),
    );
  }

  public searchInventoryItems(searchTerm: string): Observable<InventoryPaginatedResponse> {
    return this.http.get<InventoryPaginatedResponse>(
      `${this.baseUrl}${ApiEndpoints.SEARCH_INVENTORY_ITEMS}${searchTerm}`,
    );
  }

  public updateInventoryItem(id: string, data: Partial<UpdateInventoryItem>): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}${ApiEndpoints.INVENTORY_ITEMS}/${id}`, data);
  }

  public getInventoryItemById(id: string): Observable<InventoryItemFullInformation> {
    return this.http
      .get<{
        data: InventoryItemFullInformation;
      }>(`${this.baseUrl}${ApiEndpoints.INVENTORY_ITEMS}/${id}/full-info`)
      .pipe(map((response) => response.data));
  }

  public createInventoryItem(
    data: CreateInventoryItemDto,
  ): Observable<InventoryItemCreatedResponse> {
    return this.http.post<InventoryItemCreatedResponse>(
      `${this.baseUrl}${ApiEndpoints.INVENTORY_ITEMS}`,
      data,
    );
  }

  public getInventoryItemDetails(id: string): Observable<InventoryItemDetail[]> {
    return this.http
      .get<{
        data: InventoryItemDetail[];
      }>(`${this.baseUrl}${ApiEndpoints.INVENTORY_ITEMS}/${id}/details`)
      .pipe(map((response) => response.data));
  }

  public searchInventoryItemsByName(searchTerm = ''): Observable<InventoryPaginatedResponse> {
    const params = new HttpParams().set('ProductName', searchTerm);

    return this.http.get<InventoryPaginatedResponse>(
      `${this.baseUrl}${ApiEndpoints.GET_INVENTORY_ITEMS}`,
      { params },
    );
  }
}
